import { Link } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import { type ReactNode, useEffect, useState } from 'react';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
} from '@apps/web/components/admin/outreach_observability/constants';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { cn } from '@apps/web/lib/utils';
import { useAdminOutreachInFlightRuns } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type InFlightRunListItem } from '@libraries/typescript/api_client';

// The board reads "longest waiting first", so the age has to keep advancing
// between the 30s refetches. A local tick recomputes ages off created_at.
const AGE_TICK_INTERVAL_MS = 10_000;
const AGE_AMBER_MS = 10 * 60_000;
const AGE_RED_MS = 30 * 60_000;

type InFlightBoardProps = {
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
};

function formatAge(ageMs: number): string {
    const totalSeconds = Math.max(0, Math.floor(ageMs / 1000));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes < 60) {
        return `${minutes}m ${totalSeconds % 60}s`;
    }
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
}

function formatStartedAt(iso: string): string {
    return formatInTimeZone(
        new Date(iso),
        OUTREACH_ANALYTICS_TIMEZONE,
        'h:mm a',
    );
}

// Amber past 10 minutes, red past 30 — the age text names the state too, so
// severity never rides on color alone.
function ageClasses(ageMs: number): string {
    if (ageMs >= AGE_RED_MS) {
        return 'text-red-600 dark:text-red-400';
    }
    if (ageMs >= AGE_AMBER_MS) {
        return 'text-amber-600 dark:text-amber-400';
    }
    return 'text-foreground';
}

function InFlightRow({
    run,
    nowMs,
}: {
    readonly run: InFlightRunListItem;
    readonly nowMs: number;
}) {
    const ageMs = nowMs - Date.parse(run.created_at);
    return (
        <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-sm first:border-t-0">
            <div className="flex min-w-0 flex-col">
                <span className="min-w-0 truncate font-medium">
                    {run.org_name}
                </span>
                <span className="text-muted-foreground text-xs tabular-nums">
                    started {formatStartedAt(run.created_at)}
                </span>
            </div>
            <div className="flex shrink-0 items-center gap-3">
                <span
                    className={cn(
                        'text-sm font-semibold tabular-nums',
                        ageClasses(ageMs),
                    )}
                >
                    {formatAge(ageMs)}
                </span>
                {run.agent_conversation_id ? (
                    <Link
                        to="/admin/agent-traces/$conversationId"
                        params={{
                            conversationId: run.agent_conversation_id,
                        }}
                        className="text-primary text-xs hover:underline"
                    >
                        Trace
                    </Link>
                ) : null}
            </div>
        </div>
    );
}

// The in-flight runs list body: fetch + age tick + oldest-first sort + rows +
// truncated note. Shared so the board card and the hero "stuck runs" dialog
// render one implementation. Only fetches while mounted (dialog unmounts when
// closed), and SWR dedupes the shared key when the board is also on screen.
export function InFlightRunsList({
    organizationIds,
    urgency,
}: InFlightBoardProps) {
    const { data } = useAdminOutreachInFlightRuns(
        organizationIds,
        true,
        urgency,
    );
    const [nowMs, setNowMs] = useState(() => Date.now());
    useEffect(() => {
        const intervalId = setInterval(() => {
            setNowMs(Date.now());
        }, AGE_TICK_INTERVAL_MS);
        return () => {
            clearInterval(intervalId);
        };
    }, []);
    // Oldest first: the longest-waiting run is the one to look at.
    const runs = [...(data?.runs ?? [])].sort(
        (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
    );
    if (runs.length === 0) {
        return (
            <p className="text-muted-foreground py-6 text-center text-sm">
                No runs in flight.
            </p>
        );
    }
    return (
        <div className="min-h-0 flex-1">
            <div className="max-h-[26rem] overflow-y-auto rounded-md border">
                {runs.map((run) => (
                    <InFlightRow key={run.run_id} run={run} nowMs={nowMs} />
                ))}
            </div>
            {data?.truncated ? (
                // The list is oldest-first and total_matching is the true total,
                // so the newest runs (not shown) are the ones dropped.
                <p className="text-muted-foreground pt-2 text-center text-xs">
                    Showing oldest {runs.length} of {data.total_matching}.
                </p>
            ) : null}
        </div>
    );
}

export function InFlightBoard({
    organizationIds,
    urgency,
}: InFlightBoardProps) {
    const { data } = useAdminOutreachInFlightRuns(
        organizationIds,
        true,
        urgency,
    );
    return (
        <Card className="flex min-h-0 flex-col">
            <CardHeader>
                <CardTitle>In flight ({data?.total_matching ?? 0})</CardTitle>
                <CardDescription>
                    Outreach runs currently executing, longest waiting first.
                </CardDescription>
            </CardHeader>
            <CardContent className="min-h-0 flex-1">
                <InFlightRunsList
                    organizationIds={organizationIds}
                    urgency={urgency}
                />
            </CardContent>
        </Card>
    );
}

// Hero "stuck runs" drill-down: the same in-flight runs list in a modal, with
// an optional footer action (the hero passes a "Go to Agent" navigation link).
export function InFlightRunsDialog({
    open,
    onOpenChange,
    organizationIds,
    urgency,
    footer,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
    readonly footer?: ReactNode;
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>In-flight runs</DialogTitle>
                    <DialogDescription>
                        Outreach runs currently executing, longest waiting
                        first.
                    </DialogDescription>
                </DialogHeader>
                {open ? (
                    <InFlightRunsList
                        organizationIds={organizationIds}
                        urgency={urgency}
                    />
                ) : null}
                {footer ? <DialogFooter>{footer}</DialogFooter> : null}
            </DialogContent>
        </Dialog>
    );
}
